import { useEffect, useState } from "react";
import { AlertOctagon, Camera, CheckCircle2, ClipboardList, History, Loader2, Package, Pause, Play, Sparkles, Square, Timer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errorMessage } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { EmptyState, ErrorBanner, Field, Loader, Pill, StatusBadge } from "@/components/common";
import { PhotoGallery } from "@/components/photos/PhotoGallery";
import { fmtDateTime, fmtKm, fmtMinutes } from "@/lib/format";
import { WORK_STATE, WORK_STATE_CLS } from "@/lib/i18n";

function LiveTimer({ task }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(i);
  }, []);
  const open = task.time_entries?.find((e) => !e.end);
  const extra = open && open.type === "work" ? (Date.now() - new Date(open.start).getTime()) / 60000 : 0;
  const base = task.time_entries?.filter((e) => e.type === "work" && e.end).reduce((s, e) => s + (new Date(e.end) - new Date(e.start)) / 60000, 0) || 0;
  const work = Math.round(base + extra);
  const over = task.estimated_minutes && work > task.estimated_minutes;
  return (
    <div className="flex items-end gap-4" data-testid="mechanic-live-timer">
      <div>
        <div className="eyebrow">Temps travaillé</div>
        <div className={`font-heading text-5xl font-extrabold tabular ${over ? "text-red-500" : ""}`}>{fmtMinutes(work)}</div>
      </div>
      <div className="pb-1 text-sm text-muted-foreground">
        <div>Prévu : <span className="tabular font-semibold text-foreground">{fmtMinutes(task.estimated_minutes)}</span></div>
        <div>Attente : <span className="tabular font-semibold text-foreground">{fmtMinutes(task.waiting_minutes)}</span></div>
      </div>
      {over && <Pill className="mb-2 border-red-500/40 bg-red-500/10 text-red-500">Dépassement</Pill>}
    </div>
  );
}

export default function MechanicPage() {
  const { data, loading, error, reload } = useFetch(() => api.get("/repair-orders/my-tasks"), []);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const [partOpen, setPartOpen] = useState(false);
  const [problemOpen, setProblemOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const current = data?.current;
  const act = async (task, action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/repair-orders/${task.ro_id}/tasks/${task.id}/time`, { action, ...extra });
      toast.success({ start: "Tâche démarrée", pause: "Tâche mise en pause", resume: "Tâche reprise", finish: "Tâche terminée" }[action]);
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "already_running") {
        toast.warning(d.message, { duration: 8000, action: { label: "Mettre en pause et démarrer", onClick: () => act(task, action, { ...extra, auto_pause: true }) } });
      } else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <Loader label="Chargement de vos travaux…" />;
  if (error) return <ErrorBanner message={error} onRetry={reload} />;

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="mechanic-page">
      <div className="flex items-center justify-between rise">
        <div>
          <h1 className="text-3xl font-bold">Mes travaux</h1>
          <p className="text-sm text-muted-foreground">Interface atelier · gros boutons tactiles</p>
        </div>
        <Button variant="outline" onClick={reload} className="h-12 rounded-xl" data-testid="mechanic-refresh-button">Actualiser</Button>
      </div>

      {current ? (
        <section className="surface relative overflow-hidden border-2 border-primary/40 p-5 sm:p-7 rise rise-1" data-testid="mechanic-current-task">
          <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary/15 blur-3xl" />
          <div className="relative space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="eyebrow !text-primary">Travail actuel · {current.ro_number}</div>
                <h2 className="mt-1 text-2xl font-bold sm:text-3xl">{current.description}</h2>
                <div className="mt-1 text-base text-muted-foreground">{current.vehicle_label} · {current.client_name}</div>
              </div>
              <div className="flex flex-col items-end gap-2"><StatusBadge status={current.ro_status} /><Pill className={WORK_STATE_CLS[current.work_state]}>{WORK_STATE[current.work_state]}</Pill></div>
            </div>
            <LiveTimer task={current} />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {current.status === "in_progress" ? (
                <Button onClick={() => setPauseOpen(true)} disabled={busy} className="touch-btn bg-amber-500 text-black hover:bg-amber-400" data-testid="mechanic-pause-job-button"><Pause className="mr-2 h-6 w-6" /> Pause</Button>
              ) : (
                <Button onClick={() => act(current, "resume")} disabled={busy} className="touch-btn bg-emerald-600 hover:bg-emerald-500" data-testid="mechanic-resume-job-button"><Play className="mr-2 h-6 w-6" /> Reprendre</Button>
              )}
              <Button onClick={() => setFinishOpen(true)} disabled={busy} className="touch-btn bg-primary hover:bg-primary/90" data-testid="mechanic-finish-job-button"><Square className="mr-2 h-6 w-6" /> Terminer</Button>
              <Button variant="outline" onClick={() => setPartOpen(true)} className="touch-btn" data-testid="mechanic-request-part-button"><Package className="mr-2 h-6 w-6" /> Pièce</Button>
              <Button variant="outline" onClick={() => setProblemOpen(true)} className="touch-btn border-orange-500/50 text-orange-500 hover:bg-orange-500/10" data-testid="mechanic-report-problem-button"><AlertOctagon className="mr-2 h-6 w-6" /> Problème</Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Button variant="secondary" onClick={() => setReportOpen(true)} className="h-14 rounded-xl text-base" data-testid="mechanic-report-button"><Sparkles className="mr-2 h-5 w-5 text-primary" /> Compte rendu (dictée → IA)</Button>
              <Button variant="secondary" onClick={() => setPhotoOpen(true)} className="h-14 rounded-xl text-base" data-testid="mechanic-photo-button"><Camera className="mr-2 h-5 w-5" /> Photos avant / après</Button>
              <Button variant="secondary" onClick={() => setFinishOpen(true)} className="h-14 rounded-xl text-base" data-testid="mechanic-checklist-button"><ClipboardList className="mr-2 h-5 w-5" /> Checklist</Button>
            </div>
            {(current.instructions || current.vehicle_notes || current.recommendations || current.reason) && (
              <div className="grid gap-3 rounded-2xl border border-border bg-secondary/40 p-4 text-sm sm:grid-cols-2">
                <div><div className="eyebrow mb-1">Motif client</div><p>{current.reason}</p></div>
                {current.instructions && <div><div className="eyebrow mb-1">Consignes</div><p>{current.instructions}</p></div>}
                {current.vehicle_notes && <div><div className="eyebrow mb-1">Notes véhicule</div><p>{current.vehicle_notes}</p></div>}
                {current.recommendations && <div><div className="eyebrow mb-1">Recommandations</div><p>{current.recommendations}</p></div>}
                <div><div className="eyebrow mb-1">Kilométrage</div><p className="tabular">{fmtKm(current.mileage_in)}</p></div>
                <div><div className="eyebrow mb-1">Restitution prévue</div><p>{fmtDateTime(current.promised_date)}</p></div>
              </div>
            )}
            {current.history?.length > 0 && (
              <div className="rounded-2xl border border-border p-4 text-sm">
                <div className="mb-2 flex items-center gap-2 font-semibold"><History className="h-4 w-4" /> Historique utile du véhicule</div>
                {current.history.map((h) => <div key={h.number} className="text-muted-foreground">· {h.number} — {h.reason} ({h.tasks.join(", ")})</div>)}
              </div>
            )}
            {current.report && <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm"><div className="eyebrow mb-1 !text-emerald-500">Compte rendu validé</div><p className="whitespace-pre-wrap">{current.report}</p></div>}
          </div>
        </section>
      ) : (
        <section className="surface p-8 text-center rise rise-1" data-testid="mechanic-no-current">
          <Timer className="mx-auto h-10 w-10 text-muted-foreground/60" />
          <h2 className="mt-2 text-xl font-bold">Aucun travail en cours</h2>
          <p className="text-sm text-muted-foreground">Démarrez une tâche dans la liste ci-dessous.</p>
        </section>
      )}

      <section className="space-y-3 rise rise-2" data-testid="mechanic-upcoming">
        <h3 className="text-lg font-semibold">Prochaines tâches <span className="text-muted-foreground">({data.upcoming.length})</span></h3>
        {data.upcoming.length === 0 ? <EmptyState title="Rien de planifié pour l'instant" /> : data.upcoming.map((t) => (
          <div key={t.id} className="surface flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`mechanic-task-${t.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2"><span className="tabular text-xs font-bold text-primary">{t.ro_number}</span><StatusBadge status={t.ro_status} /><Pill className={WORK_STATE_CLS[t.work_state]}>{WORK_STATE[t.work_state]}</Pill></div>
              <div className="mt-1 text-lg font-semibold">{t.description}</div>
              <div className="text-sm text-muted-foreground">{t.vehicle_label} · {t.client_name} · prévu {fmtMinutes(t.estimated_minutes)}{t.promised_date ? ` · restitution ${fmtDateTime(t.promised_date)}` : ""}</div>
            </div>
            <Button onClick={() => act(t, t.status === "paused" ? "resume" : "start")} disabled={busy} className="touch-btn min-w-[180px] bg-emerald-600 hover:bg-emerald-500" data-testid="mechanic-start-job-button"><Play className="mr-2 h-6 w-6" /> {t.status === "paused" ? "Reprendre" : "Démarrer"}</Button>
          </div>
        ))}
        {current && data.upcoming.length > 0 && <p className="text-xs text-muted-foreground">Un seul chronomètre à la fois : démarrer une autre tâche vous proposera de mettre le travail actuel en pause.</p>}
      </section>

      {data.proposed.length > 0 && (
        <section className="space-y-2 rise rise-3" data-testid="mechanic-proposed">
          <h3 className="text-lg font-semibold">Travaux proposés — en attente d'accord client</h3>
          {data.proposed.map((t) => <div key={t.id} className="rounded-xl border border-orange-500/30 bg-orange-500/5 px-4 py-3 text-sm"><span className="font-semibold">{t.description}</span> <span className="text-muted-foreground">· {t.ro_number} · ne peut pas être démarré sans accord</span></div>)}
        </section>
      )}

      {data.done_today.length > 0 && (
        <section className="space-y-2 rise rise-3" data-testid="mechanic-done">
          <h3 className="text-lg font-semibold">Terminées</h3>
          {data.done_today.map((t) => <div key={t.id} className="flex items-center gap-3 rounded-xl border border-border px-4 py-3 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-500" /><span className="font-medium">{t.description}</span><span className="text-muted-foreground">· {t.ro_number} · {fmtMinutes(t.work_minutes)} travaillées</span></div>)}
        </section>
      )}

      {current && <PauseDialog open={pauseOpen} onOpenChange={setPauseOpen} reasons={data.pause_reasons} onConfirm={(r) => { setPauseOpen(false); act(current, "pause", { pause_reason: r }); }} />}
      {current && <FinishDialog open={finishOpen} onOpenChange={setFinishOpen} task={current} onDone={reload} />}
      {current && <PartRequestDialog open={partOpen} onOpenChange={setPartOpen} task={current} onDone={reload} />}
      {current && <ProblemDialog open={problemOpen} onOpenChange={setProblemOpen} task={current} onDone={reload} />}
      {current && <ReportDialog open={reportOpen} onOpenChange={setReportOpen} task={current} onDone={reload} />}
      {current && <PhotoDialog open={photoOpen} onOpenChange={setPhotoOpen} task={current} />}
    </div>
  );
}

function PhotoDialog({ open, onOpenChange, task }) {
  const meta = (phase) => ({ entity_type: "repair_order", entity_id: task.ro_id, task_id: task.id, phase });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" aria-describedby={undefined} data-testid="mechanic-photo-dialog">
        <DialogHeader><DialogTitle>Photos — {task.description}</DialogTitle></DialogHeader>
        <Tabs defaultValue="before">
          <TabsList className="grid h-12 w-full grid-cols-2"><TabsTrigger value="before" className="h-10 text-base" data-testid="mechanic-photos-tab-before">Avant travaux</TabsTrigger><TabsTrigger value="after" className="h-10 text-base" data-testid="mechanic-photos-tab-after">Après travaux</TabsTrigger></TabsList>
          <TabsContent value="before" className="pt-3"><PhotoGallery meta={meta("before")} canUpload size="lg" testId="mechanic-before-gallery" emptyText="Aucune photo avant travaux" /></TabsContent>
          <TabsContent value="after" className="pt-3"><PhotoGallery meta={meta("after")} canUpload size="lg" testId="mechanic-after-gallery" emptyText="Aucune photo après travaux" /></TabsContent>
        </Tabs>
        <p className="text-xs text-muted-foreground">Chaque photo est horodatée et signée de votre nom dans le journal du dossier. La réception décide ensuite de ce qui est partagé avec le client.</p>
      </DialogContent>
    </Dialog>
  );
}

function PauseDialog({ open, onOpenChange, reasons, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pause-dialog">
        <DialogHeader><DialogTitle>Motif de la pause</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {reasons.map((r) => <Button key={r} variant="outline" onClick={() => onConfirm(r)} className="h-16 rounded-xl text-base font-semibold" data-testid={`pause-reason-${r}`}>{r}</Button>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FinishDialog({ open, onOpenChange, task, onDone }) {
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [qc, setQc] = useState(task.qc_result || "ok");
  const [busy, setBusy] = useState(false);
  useEffect(() => setChecklist(task.checklist || []), [task]);
  const allDone = checklist.every((c) => c.done);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/repair-orders/${task.ro_id}/tasks/${task.id}`, { checklist, qc_result: qc });
      await api.post(`/repair-orders/${task.ro_id}/tasks/${task.id}/time`, { action: "finish" });
      toast.success("Intervention terminée — dossier passé en contrôle qualité si toutes les tâches sont finies");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="finish-dialog">
        <DialogHeader><DialogTitle>Checklist de fin d'intervention</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {checklist.map((c, i) => (
            <label key={i} className="flex min-h-[56px] items-center gap-3 rounded-xl border border-border px-4 text-base" data-testid={`checklist-item-${i}`}>
              <Checkbox checked={c.done} onCheckedChange={(v) => setChecklist((l) => l.map((x, j) => (j === i ? { ...x, done: !!v } : x)))} className="h-6 w-6" />
              {c.label}
            </label>
          ))}
        </div>
        <Field label="Résultat du contrôle">
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant={qc === "ok" ? "default" : "outline"} onClick={() => setQc("ok")} className="h-12" data-testid="qc-ok-button">Conforme</Button>
            <Button type="button" variant={qc === "nok" ? "destructive" : "outline"} onClick={() => setQc("nok")} className="h-12" data-testid="qc-nok-button">Point à revoir</Button>
          </div>
        </Field>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !allDone} className="h-12 w-full text-base font-bold" data-testid="finish-confirm-button">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : allDone ? "Valider et terminer" : "Complétez la checklist"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartRequestDialog({ open, onOpenChange, task, onDone }) {
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/repair-orders/${task.ro_id}/part-requests`, { task_id: task.id, description, qty: Number(qty) });
      toast.success("Demande envoyée au responsable pièces");
      setDescription("");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="part-request-dialog">
        <DialogHeader><DialogTitle>Demande de pièce</DialogTitle></DialogHeader>
        <Field label="Pièce nécessaire"><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex : plaquettes arrière, filtre à air…" className="h-12" data-testid="part-request-description" /></Field>
        <Field label="Quantité"><Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="h-12 w-32" data-testid="part-request-qty" /></Field>
        <DialogFooter><Button onClick={submit} disabled={busy || !description.trim()} className="h-12 w-full" data-testid="part-request-submit">Envoyer la demande</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProblemDialog({ open, onOpenChange, task, onDone }) {
  const [description, setDescription] = useState("");
  const [minutes, setMinutes] = useState(30);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/repair-orders/${task.ro_id}/problems`, { description, estimated_minutes: Number(minutes) });
      toast.success("Problème signalé — la réception demandera l'accord du client");
      setDescription("");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="problem-dialog">
        <DialogHeader><DialogTitle>Signaler un problème supplémentaire</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Ces travaux seront enregistrés comme « proposés » : aucun travail ne sera engagé sans accord du client.</p>
        <Field label="Constat"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Ex : flexible de frein avant gauche poreux, à remplacer" data-testid="problem-description" /></Field>
        <Field label="Temps estimé (minutes)"><Input type="number" min={5} step={5} value={minutes} onChange={(e) => setMinutes(e.target.value)} className="h-12 w-32" data-testid="problem-minutes" /></Field>
        <DialogFooter><Button onClick={submit} disabled={busy || !description.trim()} className="h-12 w-full" data-testid="problem-submit">Signaler</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ open, onOpenChange, task, onDone }) {
  const [text, setText] = useState("");
  const [structured, setStructured] = useState(null);
  const [busy, setBusy] = useState(false);
  const structure = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/structure-report", { text, repair_order_id: task.ro_id });
      setStructured(data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const render = (s) => [
    s.constat && `CONSTAT\n${s.constat}`,
    s.travaux_realises?.length && `TRAVAUX RÉALISÉS\n${s.travaux_realises.map((x) => `- ${x}`).join("\n")}`,
    s.pieces_utilisees?.length && `PIÈCES\n${s.pieces_utilisees.map((x) => `- ${x}`).join("\n")}`,
    s.recommandations?.length && `RECOMMANDATIONS\n${s.recommandations.map((x) => `- ${x}`).join("\n")}`,
    s.points_attention?.length && `POINTS D'ATTENTION\n${s.points_attention.map((x) => `- ${x}`).join("\n")}`,
    s.hypotheses_a_valider?.length && `HYPOTHÈSES À VALIDER PAR UN PROFESSIONNEL\n${s.hypotheses_a_valider.map((x) => `- ${x}`).join("\n")}`,
  ].filter(Boolean).join("\n\n");
  const [final, setFinal] = useState("");
  useEffect(() => { if (structured) setFinal(structured.raw || render(structured)); }, [structured]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/repair-orders/${task.ro_id}/tasks/${task.id}`, { report: final });
      toast.success("Compte rendu validé et enregistré");
      onOpenChange(false);
      setStructured(null);
      setText("");
      onDone();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="report-dialog">
        <DialogHeader><DialogTitle>Compte rendu d'intervention</DialogTitle></DialogHeader>
        {!structured ? (
          <>
            <p className="text-sm text-muted-foreground">Dictez ou tapez votre compte rendu en langage naturel. L'IA propose une version structurée que vous vérifiez avant validation. (La dictée vocale native sera activée à la prochaine itération : utilisez le clavier vocal de la tablette.)</p>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder="Ex : vidange faite, filtre huile et air changés, j'ai remarqué que la courroie accessoires est craquelée, à prévoir…" data-testid="report-text" />
            <DialogFooter><Button onClick={structure} disabled={busy || text.trim().length < 10} className="h-12 w-full" data-testid="report-structure-button">{busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Sparkles className="mr-2 h-5 w-5" />} Structurer avec l'IA</Button></DialogFooter>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">Proposition générée à partir de votre dictée. Corrigez librement puis validez.</p>
            <Textarea value={final} onChange={(e) => setFinal(e.target.value)} rows={12} className="font-mono text-xs" data-testid="report-structured-text" />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStructured(null)} data-testid="report-back-button">Modifier la dictée</Button>
              <Button onClick={save} disabled={busy} data-testid="report-save-button">Valider le compte rendu</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
